import React, { useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import * as Yup from "yup";
import { MRT_ColumnDef } from "material-react-table";

import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { FormConfigProps } from "../../../../../../../shared/components/form-builder/form-builder.model";
import CustomTable from "../../../../../../../shared/components/table/custom-table";
import { CustomSelect } from "../../../../../../../shared/components/inputfields/form-elements";

export const initIncomingTcStandardMap: Record<string, string[]> = {};

type IncomingTcStandardProps = {
  formRef?: any;
  componentData: Record<string, string[]>;
  setComponentData: (data: Record<string, string[]>) => void;
  selectedArticles: string[];
  availableStandards: string[];
};

const IncomingTcStandard: React.FC<IncomingTcStandardProps> = ({
  formRef,
  componentData,
  setComponentData,
  selectedArticles = [],
  availableStandards = [],
}) => {
  // Initialize map only when selected articles change (not on every render)
  useEffect(() => {
    // Only update if there are new articles not present in componentData
    const needsUpdate = selectedArticles.some(article => !(article in componentData));
    
    if (needsUpdate) {
      const updatedMap = selectedArticles.reduce<Record<string, string[]>>((acc, article) => {
        acc[article] = componentData?.[article] || [];
        return acc;
      }, {});
      setComponentData(updatedMap);
    }
  }, [selectedArticles.join(',')]); // Only trigger when articles list actually changes

  // Simple validation - just check if standards are selected
  const validationSchema = Yup.object().shape({});
  
  // Manual validation function
  const validateStandards = () => {
    const map = formRef?.current?.values?.tcIncomingStandardMap || componentData;
    const invalid = selectedArticles.filter((article) => !map[article] || map[article].length === 0);
    return invalid.length === 0;
  };

  // Memoized callback to prevent recreation on every render
  const handleStandardChange = useCallback((article: string, newValue: string[]) => {
    const currentMap = formRef?.current?.values?.tcIncomingStandardMap || componentData;
    const updated = {
      ...currentMap,
      [article]: newValue,
    };

    // Update Formik
    if (formRef?.current?.setFieldValue) {
      formRef.current.setFieldValue("tcIncomingStandardMap", updated, false);
    }
    
    // Update parent state
    setComponentData(updated);
  }, [formRef, componentData, setComponentData]);

  const columns = getColumns(selectedArticles, availableStandards, formRef, handleStandardChange);

  const formConfig: FormConfigProps = {
    initialValues: { tcIncomingStandardMap: componentData },
    validationSchema,
    sections: [
      {
        fields: [
          {
            label: "",
            name: "tcIncomingStandardMap",
            required: true,
            type: "component",
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box>
                {selectedArticles.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      No incoming products selected. Please select products in the previous step.
                    </Typography>
                  </Box>
                ) : (
                  <CustomTable
                    columns={columns}
                    data={selectedArticles.map((a) => ({
                      tcIncomingProductArticleNo: a,
                      tcIncomingTcStandard: componentData?.[a] || [],
                    }))}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enableRowActions={false}
                  />
                )}
              </Box>
            ),
          },
        ],
      },
    ],
    handleSubmit: (values) => {
      setComponentData(values.tcIncomingStandardMap || {});
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

const getColumns = (
  selectedArticles: string[],
  availableStandards: string[],
  formRef: any,
  handleStandardChange: (article: string, newValue: string[]) => void
): MRT_ColumnDef<any>[] => {
  return [
    {
      accessorKey: "tcIncomingProductArticleNo",
      header: "Incoming Product Article No",
      Cell: ({ row }) => <Typography variant="body2">{row.original.tcIncomingProductArticleNo}</Typography>,
    },
    {
      accessorKey: "tcIncomingTcStandard",
      header: "",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i>Product Standard
        </span>
      ),
      enableEditing: false,
      Cell: ({ row }) => {
        const article = row.original.tcIncomingProductArticleNo;
        const value: string[] = Array.isArray(row.original.tcIncomingTcStandard) 
          ? row.original.tcIncomingTcStandard 
          : [];

        // Simple change handler - just extract the value and call parent handler
        const handleChange = (e: any) => {
          const newValue: string[] = Array.isArray(e?.target?.value) 
            ? e.target.value.map(String) 
            : [];
          
          // Only update if value actually changed
          if (JSON.stringify(newValue.sort()) !== JSON.stringify(value.sort())) {
            handleStandardChange(article, newValue);
          }
        };

        // Ensure availableStandards is properly split into individual standards
        const standardOptions = availableStandards
          .filter(s => s != null && String(s).trim()) // Remove null/undefined/empty values
          .map((s) => {
            const standard = String(s).trim();
            return { value: standard, label: standard };
          });

        return (
          <CustomSelect
            value={value}
            options={standardOptions}
            multiple={true}
            enableSearch={false}
            placeholder="Select Product Standards"
            onChange={handleChange}
          />
        );
      },
    },
  ];
};

export default IncomingTcStandard;